import { Figure, svgType } from './Figure'

/**
 * Net worth as the sum of signed account balances.
 *
 * Drawn around a zero line rather than as a pie, because the point is that
 * debts are stored credit-normal — negative — and simply add. The arithmetic
 * is what `household_net_worth` (migration 023) produces for these balances:
 * 412,000 + 186,000 + (−329,000) = 269,000.
 */
export function NetWorthDiagram() {
  // px per dollar. Chosen so neither the widest positive band nor the debt
  // band runs off its side of the zero line.
  // Leaves room for the amount label to the right of the widest bar without
  // running past the viewBox.
  const SCALE = 0.00082
  const ZERO_X = 300

  const bands = [
    { label: 'Assets', sub: 'chequing, savings, the house', amount: 412000, colour: 'var(--inflow)' },
    { label: 'Investments', sub: 'RRSP, TFSA, non-registered', amount: 186000, colour: 'var(--invest)' },
    { label: 'Debts', sub: 'mortgage, cards, line of credit', amount: -329000, colour: 'var(--outflow)' },
  ]

  const money = (n: number) =>
    `${n < 0 ? '−' : '+'}${Math.abs(n).toLocaleString('en-CA')}`

  return (
    <Figure
      title="Net worth as a sum: assets 412,000 plus investments 186,000 plus debts negative 329,000 equals 269,000."
      caption="Nothing subtracts here. A liability carries a negative balance in the ledger, so it can neither be double-counted nor forgotten by a report that omits the minus sign."
    >
      <svg viewBox="0 0 720 322" xmlns="http://www.w3.org/2000/svg">
        {/* Zero line. Every band is measured from it, in both directions. */}
        <line x1={ZERO_X} y1="8" x2={ZERO_X} y2="212" stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3" />

        {bands.map((band, i) => {
          const top = 16 + i * 70
          const width = Math.abs(band.amount) * SCALE
          const barX = band.amount < 0 ? ZERO_X - width : ZERO_X
          return (
            <g key={band.label}>
              <text x="0" y={top} {...svgType.label}>{band.label}</text>
              <text x="0" y={top + 16} {...svgType.faint}>{band.sub}</text>
              <rect x={barX} y={top + 24} width={width} height="16" rx="2" fill={band.colour} opacity="0.85" />
              <text
                x={band.amount < 0 ? ZERO_X + 10 : barX + width + 10}
                y={top + 36}
                {...svgType.numSmall}
                fill="var(--text-faint)"
              >
                {money(band.amount)}
              </text>
            </g>
          )
        })}

        <line x1="0" y1="234" x2="720" y2="234" stroke="var(--border)" strokeWidth="1" />

        <text x="0" y="262" {...svgType.label}>Net worth</text>
        <rect x={ZERO_X} y="252" width={269000 * SCALE} height="16" rx="2" fill="var(--text)" opacity="0.7" />
        <text x={ZERO_X + 269000 * SCALE + 10} y="264" {...svgType.num} fill="var(--text)">
          269,000
        </text>

        <text x="0" y="300" {...svgType.faint}>
          Income, Expense and Equity balances are excluded from this total.
        </text>
        <text x="0" y="316" {...svgType.faint}>
          They are bookkeeping counterparties, not things the household owns or owes.
        </text>
      </svg>
    </Figure>
  )
}
